import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import map from "lodash/fp/map";
import reject from "lodash/fp/reject";
import sum from "lodash/fp/sum";

import { AnalyticsOrder } from "utils/analytics/analytics.utils";
import { fromCents } from "utils/price.utils";

import { TrackOrderFragment } from "../track-order.query.generated";

import { fromOrderItem } from "./analytics-product.mapper";

const DELIVERY_FEE_SKU_PREFIX = "DELFEE";

const isShipping = ({ item }: TrackOrderFragment["orderItems"][number]) =>
  item.sku.startsWith(DELIVERY_FEE_SKU_PREFIX);

const getShipping: (order: TrackOrderFragment) => number = flow(
  get("orderItems"),
  filter(isShipping),
  map("buyingPriceInCents"),
  sum,
);

const getSubtotal: (order: TrackOrderFragment) => number = flow(
  get("orderItems"),
  reject(isShipping),
  map("buyingPriceInCents"),
  sum,
);

const getTax: (order: TrackOrderFragment) => number = flow(
  get("orderItems"),
  map("taxAmountInCents"),
  sum,
);

const getDiscount: (order: TrackOrderFragment) => number = flow(
  get("orderItems"),
  map("buyingPriceInCents"),
  filter((price) => price < 0),
  sum,
);

const getTotal = (order: TrackOrderFragment) =>
  getSubtotal(order) + getShipping(order) + getTax(order);

export const fromOrder = (order: TrackOrderFragment): AnalyticsOrder => ({
  order_id: order.id,
  subtotal: fromCents(getSubtotal(order)),
  revenue: fromCents(getSubtotal(order)), // same as subtotal
  value: fromCents(getSubtotal(order)), // same as subtotal
  shipping: fromCents(getShipping(order)),
  total: fromCents(getTotal(order)),
  tax: fromCents(getTax(order)),
  discount: fromCents(getDiscount(order)),
  products: order.orderItems
    .filter(({ buyingPriceInCents }) => buyingPriceInCents !== 0)
    .map(fromOrderItem),
  currency: "EUR",
});
